import React, { useState, useEffect, useMemo } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faPlus,
    faChevronLeft,
    faChevronRight,
} from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router-dom";
import { PermissionWrapper } from "../../helpers/permission.js";
import { API_URL } from "../../config";
import axios from "axios";
import LoadingSpinner from "../../components/LoadingSpinner";

const BroadcastReport = () => {
    const navigate = useNavigate();

    const [campaignData, setCampaignData] = useState([]);
    const [loading, setLoading] = useState(false);

    // 🔎 Search Filters
    const [filters, setFilters] = useState({
        name: "",
        campaignName: "",
    });

    // 📑 Pagination
    const [page, setPage] = useState(1);
    const [pageCount, setPageCount] = useState(1);
    const [total, setTotal] = useState(0);
    const limit = 100;

    // Modal state
    const [showModal, setShowModal] = useState(false);
    const [modalContent, setModalContent] = useState("");
    const [filterLoading, setFilterLoading] = useState(false);
    // 🧩 Fetch Data from API
    useEffect(() => {
        const fetchCampaigns = async () => {
            try {
                setLoading(true);
                const token = localStorage.getItem("authToken");
                const response = await axios.get(
                    `${API_URL}broadcast/report?page=${page}&limit=${limit}`,
                    {
                        headers: {
                            Authorization: `Bearer ${token}`,
                            Accept: "*/*",
                        },
                    }
                );

                if (response.data && response.data.campaigns) {
                    // Map API data to match UI structure
                    const formatted = response.data.campaigns.map((item) => ({
                        id: item.id,
                        name: item.template_name || "",
                        campaignName: item.campaign_name || "",
                        createdOn: item.created_on
                            ? (() => {
                                const d = new Date(item.created_on);
                                const day = String(d.getDate()).padStart(2, "0");
                                const month = String(d.getMonth() + 1).padStart(2, "0");
                                const year = d.getFullYear();
                                return `${day}-${month}-${year}`;
                            })()
                            : "-",
                        // Additional data that might be needed for details page
                        rawData: item,
                    }));

                    setCampaignData(formatted);
                    setTotal(response.data.totalCount || 0);
                    setPageCount(Math.ceil((response.data.totalCount || 0) / limit));
                }
            } catch (error) {
                console.error("Error fetching broadcast data:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchCampaigns();
    }, [page]);

    useEffect(() => {
        if (!campaignData.length) return;

        setFilterLoading(true);

        const timer = setTimeout(() => {
            setFilterLoading(false);
        }, 300);

        return () => clearTimeout(timer);
    }, [filters, campaignData]);

    // 🔎 Apply filters
    const filteredData = useMemo(() => {
        return campaignData.filter((item) => {
            // Name filter (template_name)
            if (
                filters.name &&
                !(item.name || "").toLowerCase().includes(filters.name.toLowerCase())
            ) {
                return false;
            }

            // Campaign Name filter (campaign_name)
            if (
                filters.campaignName &&
                !item.campaignName
                    .toLowerCase()
                    .includes(filters.campaignName.toLowerCase())
            ) {
                return false;
            }

            return true;
        });
    }, [campaignData, filters]);

    // 📑 Update pagination when filters change (for client-side filtering)
    useEffect(() => {
        if (filters.name || filters.campaignName) {
            // If filters are applied, use client-side filtered data
            setTotal(filteredData.length);
            setPageCount(Math.ceil(filteredData.length / limit));
            setPage(1);
        } else {
            // If no filters, use API pagination
            setTotal(campaignData.length);
            setPageCount(Math.ceil(campaignData.length / limit));
        }
    }, [filteredData, campaignData, filters]);

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= pageCount) {
            setPage(newPage);
        }
    };

    // Determine which data to show based on filters
    const dataToShow = useMemo(() => {
        if (filters.name || filters.campaignName) {
            // Client-side filtering
            const start = (page - 1) * limit;
            const end = start + limit;
            return filteredData.slice(start, end);
        } else {
            // Server-side pagination
            return campaignData;
        }
    }, [page, filteredData, campaignData, filters]);

    const start = (page - 1) * limit + 1;
    const end = Math.min(page * limit, total);

    const handleFilterChange = (e) => {
        setFilters({ ...filters, [e.target.name]: e.target.value });
    };

    const clearFilters = () => {
        setFilters({
            name: "",
            campaignName: "",
        });
    };

    useEffect(() => {
        const handleClearFilters = () => {
            clearFilters();
        };
        window.addEventListener('clearBroadCastReportFilterEvent', handleClearFilters);

        return () => {
            window.removeEventListener('clearBroadCastReportFilterEvent', handleClearFilters);
        };
    }, []);

    const handleExport = () => {
        setShowModal(true);
        setModalContent(
            <div>
                <h5>Export Broadcast Report</h5>
                <p>Your report will be generated and downloaded shortly.</p>
                <button onClick={closeModal} className="btn btn-primary">
                    OK
                </button>
            </div>
        );
    };

    const closeModal = () => {
        setShowModal(false);
        setModalContent("");
    };



    return (
        <div
            id="page-container"
            className="page-header-fixed page-sidebar-fixed fade in"
        >
            <div id="content" className="">
                <div className="row pb-2">
                    <div className="col-md-6">
                        <h4 className="panel-title pull-left">Broadcast Report</h4>
                    </div>
                    {/* <div className="col-md-6 text-right">
                        <PermissionWrapper module="report_broadcast" action="export">
                            <button onClick={handleExport} className="btn btn-default btn-sm">
                                <FontAwesomeIcon icon={faPlus} /> Export Report
                            </button>
                        </PermissionWrapper>
                    </div> */}
                </div>

                <div className="panel panel-default with-rounded-corner with-shadow">
                    {/* 📑 Pagination Controls */}
                    {campaignData.length > 0 && (
                        <div className="d-flex align-items-center justify-content-between p-3 border-bottom">
                            <div className="panel-heading border-0">
                                <h4 className="panel-title m-0">Broadcast Reports</h4>
                            </div>
                            <div className="d-inline-block">
                                <span style={{ fontSize: "0.8rem" }} className="me-2">
                                    {start} to {end} of {total}
                                </span>
                                <button
                                    className="btn btn-default btn-sm"
                                    title="Previous"
                                    onClick={() => handlePageChange(page - 1)}
                                    disabled={page === 1}
                                >
                                    <FontAwesomeIcon icon={faChevronLeft} />
                                </button>
                                &nbsp;
                                <button
                                    className="btn btn-default btn-sm"
                                    title="Next"
                                    onClick={() => handlePageChange(page + 1)}
                                    disabled={page === pageCount}
                                >
                                    <FontAwesomeIcon icon={faChevronRight} />
                                </button>
                            </div>
                        </div>
                    )}

                    <div className="table-responsive user-table-container">
                        <style>
                            {`
                .user-table-container {
                  position: relative;
                  max-height: 74vh;
                  overflow-y: auto;
                  overflow-x: auto;
                }

                /* First header row (titles) */
                .user-table-container table thead tr:nth-child(1) th {
                  position: sticky;
                  top: 0;
                  z-index: 30;
                  background: #f8f9fa;
                }

                /* Second header row (search inputs) */
                .user-table-container table thead tr:nth-child(2) th {
                  position: sticky;
                  top: 25px; /* adjust based on header height */
                  z-index: 25;
                  background: #fff;
                }
              `}
                        </style>
                        <table className="table m-b-0">
                            <thead>
                                <tr>
                                    <th style={{ textAlign: "center" }}>#</th>
                                    <th>Name</th>
                                    <th>Campaign Name</th>
                                    <th>Created On</th>
                                </tr>
                                {/* 🔎 Search Filters Row */}
                                <tr>
                                    <th style={{ textAlign: "center" }}></th>
                                    <th>
                                        <input
                                            type="text"
                                            className="form-control input-sm"
                                            placeholder="Search name..."
                                            name="name"
                                            value={filters.name}
                                            onChange={handleFilterChange}
                                        />
                                    </th>
                                    <th>
                                        <input
                                            type="text"
                                            className="form-control input-sm"
                                            placeholder="Search campaign..."
                                            name="campaignName"
                                            value={filters.campaignName}
                                            onChange={handleFilterChange}
                                        />
                                    </th>
                                    <th>
                                        <button
                                            className="btn btn-default btn-sm"
                                            onClick={clearFilters}
                                            title="Clear filters"
                                        >
                                            Clear
                                        </button>
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading || filterLoading ? (
                                    Array.from({ length: 10 }).map((_, rowIndex) => (
                                        <tr key={rowIndex} className="skeleton-row">
                                            {Array.from({ length: 4 }).map((_, colIndex) => (
                                                <td key={colIndex}>
                                                    <div className="skeleton-box"></div>
                                                </td>
                                            ))}
                                        </tr>
                                    ))
                                ) : dataToShow.length > 0 ? (
                                    dataToShow.map((campaign, index) => (
                                        <tr
                                            key={campaign.id}
                                            style={{ cursor: "pointer" }}
                                            onClick={() =>
                                                navigate(`/Report/Broadcast/details/${campaign.id}`)
                                            }
                                        >
                                            <td style={{ textAlign: "center", verticalAlign: "middle" }}>
                                                {filters.name || filters.campaignName
                                                    ? (page - 1) * limit + index + 1
                                                    : start + index}
                                            </td>
                                            <td>{campaign.name}</td>
                                            <td>{campaign.campaignName}</td>
                                            <td>{campaign.createdOn}</td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="4" style={{ textAlign: "center" }}>
                                            No Records Found
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                {showModal && (
                    <div
                        id="myModal"
                        className="modal"
                        style={{
                            position: "fixed",
                            top: 0,
                            left: 0,
                            width: "100%",
                            height: "100%",
                            backgroundColor: "rgba(0,0,0,0.5)",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            zIndex: 9999,
                        }}
                    >
                        <div className="modal-dialog panel panel-default panel-border top">
                            <div
                                className="modal-content"
                                style={{
                                    backgroundColor: "white",
                                    padding: "20px",
                                    borderRadius: "5px",
                                    minWidth: "300px",
                                }}
                            >
                                {modalContent}
                            </div>
                        </div>
                    </div>
                )}
            </div>

            <a href="#" data-click="scroll-top" className="btn-scroll-top fade">
                <i className="ti-arrow-up"></i>
            </a>
        </div>
    );
};

export default BroadcastReport;